import { ListingCardProps } from '../components/ListingCard'

/**
 * Apparel and fragrance both sell on *choice* rather than specification — a
 * shirt is a size and a colour, a fragrance is a bottle size — and both
 * choices are small enough to make on the card itself. That is why neither
 * needs an order sheet the way a made-to-order dish does.
 */

export interface ApparelColour {
  id: string
  label: string
  /** Rendered as the swatch. */
  hex: string
  image: string
}

export interface ApparelSize {
  label: string
  inStock: boolean
}

export interface Apparel {
  brand: string
  /** "Linen · Relaxed fit" — the two things that decide a purchase. */
  material: string
  colours: ApparelColour[]
  sizes: ApparelSize[]
  price: number
  /** Present only when the item is reduced. */
  wasPrice?: number
  sizeNote?: string
}

export interface PerfumeSize {
  id: string
  /** "50 ml" */
  label: string
  price: number
  inStock: boolean
  note?: string
}

export interface Perfume {
  house: string
  /** "Eau de Parfum" */
  concentration: string
  family: string
  /** Top / heart / base, as a shopper reads them. */
  notes: string[]
  longevity: string
  sizes: PerfumeSize[]
  /** Tints the card ground so each fragrance reads as its own bottle. */
  tint: string
  /**
   * False when the house has not switched on payments — the listing is still
   * browsable, but the sale is closed in conversation rather than in a cart.
   */
  ordering?: boolean
}

const SHIRT_SAND =
  'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?auto=format&fit=crop&q=80&w=800'
const SHIRT_WHITE =
  'https://images.unsplash.com/photo-1603252109303-2751441dd157?auto=format&fit=crop&q=80&w=800'
const ABAYA =
  'https://images.unsplash.com/photo-1612722432474-b971cdcea546?auto=format&fit=crop&q=80&w=800'
const ABAYA_2 =
  'https://images.unsplash.com/photo-1595777457583-95e059d581b8?auto=format&fit=crop&q=80&w=800'
const SNEAKER =
  'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?auto=format&fit=crop&q=80&w=800'
const SNEAKER_2 =
  'https://images.unsplash.com/photo-1549298916-b41d501d3772?auto=format&fit=crop&q=80&w=800'
const JACKET =
  'https://images.unsplash.com/photo-1551028719-00167b16eac5?auto=format&fit=crop&q=80&w=800'
const JACKET_2 =
  'https://images.unsplash.com/photo-1520975954732-35dd22299614?auto=format&fit=crop&q=80&w=800'
const DRESS =
  'https://images.unsplash.com/photo-1572804013309-59a88b7e92f1?auto=format&fit=crop&q=80&w=800'
const KNIT =
  'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?auto=format&fit=crop&q=80&w=800'

export const apparelListings: (ListingCardProps & {
  apparel: Apparel
})[] = [
  {
    id: 'a1',
    title: 'Relaxed Linen Shirt',
    price: 'AED 249',
    location: 'Atelier Nine · Dubai Mall',
    images: [SHIRT_SAND, SHIRT_WHITE],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    apparel: {
      brand: 'Atelier Nine',
      material: '100% linen · Relaxed fit',
      price: 249,
      sizeNote: 'Runs generous — size down for a closer fit',
      colours: [
        {
          id: 'sand',
          label: 'Sand',
          hex: '#D8C9B0',
          image: SHIRT_SAND,
        },
        {
          id: 'white',
          label: 'Optic white',
          hex: '#F5F5F3',
          image: SHIRT_WHITE,
        },
      ],
      sizes: [
        {
          label: 'XS',
          inStock: false,
        },
        {
          label: 'S',
          inStock: true,
        },
        {
          label: 'M',
          inStock: true,
        },
        {
          label: 'L',
          inStock: true,
        },
        {
          label: 'XL',
          inStock: true,
        },
      ],
    },
  },
  {
    id: 'a2',
    title: 'Embroidered Crepe Abaya',
    price: 'AED 690',
    location: 'Maison Layal · City Walk',
    images: [ABAYA, ABAYA_2],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    apparel: {
      brand: 'Maison Layal',
      material: 'Japanese crepe · Hand embroidery',
      price: 690,
      wasPrice: 890,
      colours: [
        {
          id: 'black',
          label: 'Black',
          hex: '#1B1B1F',
          image: ABAYA,
        },
        {
          id: 'stone',
          label: 'Stone',
          hex: '#B9B2A6',
          image: ABAYA_2,
        },
      ],
      sizes: [
        {
          label: '52',
          inStock: true,
        },
        {
          label: '54',
          inStock: true,
        },
        {
          label: '56',
          inStock: true,
        },
        {
          label: '58',
          inStock: false,
        },
      ],
    },
  },
  {
    id: 'a3',
    title: 'Leather Court Sneakers',
    price: 'AED 420',
    location: 'Studio Form · Mall of the Emirates',
    images: [SNEAKER, SNEAKER_2],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    apparel: {
      brand: 'Studio Form',
      material: 'Full-grain leather · Rubber cup sole',
      price: 420,
      sizeNote: 'True to size',
      colours: [
        {
          id: 'offwhite',
          label: 'Off white',
          hex: '#EFEBE4',
          image: SNEAKER,
        },
        {
          id: 'navy',
          label: 'Navy',
          hex: '#2A3247',
          image: SNEAKER_2,
        },
      ],
      sizes: [
        {
          label: '40',
          inStock: true,
        },
        {
          label: '41',
          inStock: true,
        },
        {
          label: '42',
          inStock: false,
        },
        {
          label: '43',
          inStock: true,
        },
        {
          label: '44',
          inStock: true,
        },
      ],
    },
  },
  {
    id: 'a4',
    title: 'Suede Bomber Jacket',
    price: 'AED 1,150',
    location: 'Atelier Nine · Dubai Mall',
    images: [JACKET, JACKET_2],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    apparel: {
      brand: 'Atelier Nine',
      material: 'Goat suede · Quilted lining',
      price: 1150,
      colours: [
        {
          id: 'tobacco',
          label: 'Tobacco',
          hex: '#8A5A38',
          image: JACKET,
        },
        {
          id: 'olive',
          label: 'Olive',
          hex: '#5B5F45',
          image: JACKET_2,
        },
      ],
      sizes: [
        {
          label: 'S',
          inStock: true,
        },
        {
          label: 'M',
          inStock: true,
        },
        {
          label: 'L',
          inStock: true,
        },
        {
          label: 'XL',
          inStock: false,
        },
      ],
    },
  },
  {
    id: 'a5',
    title: 'Bias-Cut Slip Dress',
    price: 'AED 520',
    location: 'Maison Layal · City Walk',
    images: [DRESS, KNIT],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    apparel: {
      brand: 'Maison Layal',
      material: 'Sandwashed silk · Bias cut',
      price: 520,
      colours: [
        {
          id: 'champagne',
          label: 'Champagne',
          hex: '#E4D5BE',
          image: DRESS,
        },
        {
          id: 'ink',
          label: 'Ink',
          hex: '#23262E',
          image: KNIT,
        },
      ],
      sizes: [
        {
          label: 'XS',
          inStock: true,
        },
        {
          label: 'S',
          inStock: true,
        },
        {
          label: 'M',
          inStock: false,
        },
        {
          label: 'L',
          inStock: true,
        },
      ],
    },
  },
  {
    id: 'a6',
    title: 'Merino Knit Polo',
    price: 'AED 340',
    location: 'Studio Form · Mall of the Emirates',
    images: [KNIT, SHIRT_WHITE],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    apparel: {
      brand: 'Studio Form',
      material: 'Extra-fine merino · Regular fit',
      price: 340,
      colours: [
        {
          id: 'oat',
          label: 'Oat',
          hex: '#D9CDBB',
          image: KNIT,
        },
        {
          id: 'white2',
          label: 'White',
          hex: '#F5F5F3',
          image: SHIRT_WHITE,
        },
      ],
      sizes: [
        {
          label: 'S',
          inStock: true,
        },
        {
          label: 'M',
          inStock: true,
        },
        {
          label: 'L',
          inStock: true,
        },
        {
          label: 'XL',
          inStock: true,
        },
      ],
    },
  },
]

const OUD =
  'https://images.unsplash.com/photo-1592945403244-b3fbafd7f539?auto=format&fit=crop&q=80&w=800'
const AMBER =
  'https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&q=80&w=800'
const FLORAL =
  'https://images.unsplash.com/photo-1588405748880-12d1d2a59d75?auto=format&fit=crop&q=80&w=800'
const FRESH =
  'https://images.unsplash.com/photo-1615634260167-c8cdede054de?auto=format&fit=crop&q=80&w=800'
const MUSK =
  'https://images.unsplash.com/photo-1594035910387-fea47794261f?auto=format&fit=crop&q=80&w=800'
const GIFT =
  'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?auto=format&fit=crop&q=80&w=800'

export const perfumeListings: (ListingCardProps & {
  perfume: Perfume
})[] = [
  {
    id: 'p1',
    title: 'Oud Royale',
    price: 'AED 690',
    location: 'Maison Aroma · DIFC',
    images: [OUD],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    perfume: {
      house: 'Maison Aroma',
      concentration: 'Extrait de Parfum',
      family: 'Woody Oriental',
      notes: ['Cambodian oud', 'Saffron', 'Rose', 'Sandalwood'],
      longevity: '10–12 hours',
      tint: '#F3EBE2',
      sizes: [
        {
          id: 'p1-50',
          label: '50 ml',
          price: 690,
          inStock: true,
        },
        {
          id: 'p1-100',
          label: '100 ml',
          price: 1180,
          inStock: true,
          note: 'Best value',
        },
        {
          id: 'p1-travel',
          label: '10 ml travel',
          price: 190,
          inStock: true,
        },
      ],
    },
  },
  {
    id: 'p2',
    title: 'Amber Nuit',
    price: 'AED 520',
    location: 'Maison Aroma · DIFC',
    images: [AMBER],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    perfume: {
      house: 'Maison Aroma',
      concentration: 'Eau de Parfum',
      family: 'Amber Spicy',
      notes: ['Amber', 'Tonka bean', 'Cardamom', 'Vanilla'],
      longevity: '8–10 hours',
      tint: '#F0E6D6',
      sizes: [
        {
          id: 'p2-50',
          label: '50 ml',
          price: 520,
          inStock: true,
        },
        {
          id: 'p2-100',
          label: '100 ml',
          price: 840,
          inStock: true,
          note: 'Best value',
        },
      ],
    },
  },
  {
    id: 'p3',
    title: 'Rose Taif',
    price: 'AED 470',
    location: 'Attar House · Al Wasl',
    images: [FLORAL],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    perfume: {
      house: 'Attar House',
      concentration: 'Eau de Parfum',
      family: 'Floral',
      notes: ['Taif rose', 'Peony', 'Lychee', 'White musk'],
      longevity: '6–8 hours',
      tint: '#F6E7EA',
      ordering: false,
      sizes: [
        {
          id: 'p3-30',
          label: '30 ml',
          price: 290,
          inStock: true,
        },
        {
          id: 'p3-75',
          label: '75 ml',
          price: 470,
          inStock: true,
        },
        {
          id: 'p3-100',
          label: '100 ml',
          price: 610,
          inStock: false,
        },
      ],
    },
  },
  {
    id: 'p4',
    title: 'Citrus Marine',
    price: 'AED 320',
    location: 'Attar House · Al Wasl',
    images: [FRESH],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    perfume: {
      house: 'Attar House',
      concentration: 'Eau de Toilette',
      family: 'Fresh Citrus',
      notes: ['Bergamot', 'Sea salt', 'Vetiver', 'Cedar'],
      longevity: '4–6 hours',
      tint: '#E4EEF2',
      ordering: false,
      sizes: [
        {
          id: 'p4-50',
          label: '50 ml',
          price: 320,
          inStock: true,
        },
        {
          id: 'p4-100',
          label: '100 ml',
          price: 480,
          inStock: true,
        },
      ],
    },
  },
  {
    id: 'p5',
    title: 'White Musk Intense',
    price: 'AED 395',
    location: 'Maison Aroma · DIFC',
    images: [MUSK],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    perfume: {
      house: 'Maison Aroma',
      concentration: 'Eau de Parfum',
      family: 'Musky',
      notes: ['White musk', 'Iris', 'Cashmeran', 'Amberwood'],
      longevity: '8–10 hours',
      tint: '#EFEFF1',
      sizes: [
        {
          id: 'p5-50',
          label: '50 ml',
          price: 395,
          inStock: true,
        },
        {
          id: 'p5-100',
          label: '100 ml',
          price: 640,
          inStock: true,
        },
      ],
    },
  },
  {
    id: 'p6',
    title: 'Discovery Gift Set',
    price: 'AED 420',
    location: 'Maison Aroma · DIFC',
    images: [GIFT],
    specs: [],
    matchScore: 0,
    sellerType: 'Verified Business',
    perfume: {
      house: 'Maison Aroma',
      concentration: 'Gift Set',
      family: 'Mixed',
      notes: ['Oud Royale', 'Amber Nuit', 'White Musk', 'Rose Taif'],
      longevity: 'Varies',
      tint: '#F1EDE6',
      sizes: [
        {
          id: 'p6-4',
          label: '4 × 10 ml',
          price: 420,
          inStock: true,
          note: 'Gift wrapped',
        },
        {
          id: 'p6-6',
          label: '6 × 10 ml',
          price: 590,
          inStock: true,
        },
      ],
    },
  },
]

// A denim listing carries a full waist run — the case that proves the card
// stays one line while the fitting room shows everything.
apparelListings.push({
  id: 'a7',
  title: 'Straight-Leg Selvedge Jeans',
  price: 'AED 460',
  location: 'Studio Form · Mall of the Emirates',
  images: [SHIRT_WHITE],
  specs: [],
  matchScore: 0,
  sellerType: 'Verified Business',
  apparel: {
    brand: 'Studio Form',
    material: '14oz Japanese selvedge · Straight leg',
    price: 460,
    sizeNote: 'Raw denim — expect a half-inch of stretch',
    colours: [
      {
        id: 'indigo',
        label: 'Raw indigo',
        hex: '#2E3C55',
        image: SHIRT_WHITE,
      },
      {
        id: 'washed',
        label: 'Washed black',
        hex: '#2B2B2E',
        image: JACKET_2,
      },
    ],
    sizes: [
      {
        label: '28',
        inStock: true,
      },
      {
        label: '29',
        inStock: false,
      },
      {
        label: '30',
        inStock: true,
      },
      {
        label: '31',
        inStock: true,
      },
      {
        label: '32',
        inStock: true,
      },
      {
        label: '33',
        inStock: true,
      },
      {
        label: '34',
        inStock: true,
      },
      {
        label: '36',
        inStock: false,
      },
      {
        label: '38',
        inStock: true,
      },
      {
        label: '40',
        inStock: true,
      },
    ],
  },
})

export const apparelNarrowed = apparelListings.slice(0, 3)
export const perfumeNarrowed = perfumeListings.slice(0, 3)
